// PRD-1358 — shared predicates for the document field review (HITL) state.
//
// Both of these were previously inlined at several call sites, which is how the
// poll gate below came to omit the review bucket in the first place. Keeping
// them here, tested, means the card, the details modal and the polling loop
// cannot drift apart again.
//
// Vocabulary reminder (see utils/datasetBadge.js): the badge token is
// `review_needed`, while the per-file status AND the count bucket are both
// `awaiting_review`. These functions read the COUNT bucket, never the badge —
// badge precedence is processing > failed > review, so a dataset with a parked
// file and a failure carries `action_needed` and would be missed by a badge test.

/**
 * A dataset holding files parked for review, with nothing still in flight.
 *
 * The `processing === 0` half matters: mid-batch the review entry points are
 * deliberately withheld, because a reviewer cannot usefully start until the
 * batch settles. So "has parked files" alone is not the question the UI asks —
 * it asks "has parked files AND is done working".
 */
export function isAwaitingReviewOnly(dataset) {
  const counts = dataset?.file_counts || {};
  return (counts.awaiting_review ?? 0) > 0 && (counts.processing ?? 0) === 0;
}

/**
 * Whether this dataset still has state worth re-fetching, i.e. whether the
 * Data Sources polling loop should keep running.
 *
 * The review term is load-bearing and easy to talk yourself out of: document
 * field review never touches `job_status`, and an all-parked dataset reports
 * `job_status: 'complete'` by design, so every other term here goes false the
 * moment the batch settles. Without it, a PUBLISHED dataset whose files have
 * all parked stops refreshing entirely — its counts then sit stale until the
 * user reloads by hand. A DRAFT one happens to be covered by the auto-publish
 * term below, which is why the gap is easy to miss when testing.
 *
 * Note this reads `awaiting_review` directly rather than via
 * isAwaitingReviewOnly: while files are still processing we DO want to keep
 * polling, so the `processing === 0` half must not apply here.
 *
 * @param {object} dataset
 * @param {{ autoPublishEnabled?: boolean }} [options]
 */
export function isDatasetActive(dataset, { autoPublishEnabled = false } = {}) {
  const status = (dataset?.job_status || '').toLowerCase();
  // PRD-979 (gated): 'draft' rows haven't spawned an ExtractionJob yet (files
  // still uploading client-side), so job_status is null — but they still need
  // polling so "Uploading files…" flips to Processing/Ready without a refresh.
  if (autoPublishEnabled && dataset?.status === 'draft') return true;
  if (status === 'processing' || status === 'queued' || status === 'awaiting_input') return true;
  return (dataset?.file_counts?.awaiting_review ?? 0) > 0;
}

export default { isAwaitingReviewOnly, isDatasetActive };
